import React, { useState } from 'react'
import { usePlan } from '../../../context/PlanContext'
import { useQuery } from '@apollo/client'
import { GET_CLIENT_PACKAGE_HISTORY_BY_BG_ACCOUNT } from '../../../graphql/query'
import { v4 as uuidv4 } from 'uuid'

const Index = () => {
  const { currentPlan } = usePlan()
  const [currentPage, setCurrentPage] = useState(1)
  const accountPerPage = 10
  const accountPerPageOffset = (currentPage - 1) * accountPerPage
  const { loading, error, data } = useQuery(GET_CLIENT_PACKAGE_HISTORY_BY_BG_ACCOUNT, {
    variables: {
      input: {
        limit: accountPerPage,
        offset: accountPerPageOffset,
        bgAccountId: currentPlan?.bgAccountId
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'AdminPermission'
      }
    }
  })

  if (loading) {
    return <>Loading.....</>
  }
  if (error) {
    console.log(error)
    return <>Error loading data</>
  }

  const plansData = data?.getClientPackageHistoryByBgAccount?.allClientPackageHistory || []
  const totalCount = data?.getClientPackageHistoryByBgAccount?.totalCount
  const totalPages = Math.ceil(totalCount / accountPerPage)

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  // Generate snake handle pagination numbers
  const generatePaginationNumbers = () => {
    let numbers: number[] = []
    const maxVisibleNumbers = 5 // Maximum visible pagination numbers

    if (totalPages <= maxVisibleNumbers) {
      // If total pages are less than or equal to max visible numbers, show all
      for (let i = 1; i <= totalPages; i++) {
        numbers.push(i)
      }
    } else {
      const middleIndex = Math.ceil(maxVisibleNumbers / 2) // Middle index of the pagination numbers
      let startPage = currentPage - middleIndex + 1
      let endPage = currentPage + middleIndex - 1

      if (startPage < 1) {
        // If start page is less than 1, adjust start and end page accordingly
        endPage = startPage + maxVisibleNumbers - 1
        startPage = 1
      } else if (endPage > totalPages) {
        // If end page is greater than total pages, adjust start and end page accordingly
        startPage = endPage - maxVisibleNumbers + 1
        endPage = totalPages
      }

      for (let i = startPage; i <= endPage; i++) {
        numbers.push(i)
      }
    }

    return numbers
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'badge badge-light-success'
      case 'PENDING':
        return 'badge badge-light-warning'
      default:
        return 'badge badge-light-danger'
    }
  }

  const renderTableRows = (data: any) => {
    return data.map((row: any, index: any) => (
      <tr key={uuidv4()}>
        <td>{row?.packageName?.replace('_', ' ')}</td>
        <td>{new Date(parseInt(row.startDate)).toLocaleDateString()}</td>
        <td>{new Date(parseInt(row.endDate)).toLocaleDateString()}</td>
        <td>
          <div className={getStatusClass(row.status)}>{row.status}</div>
        </td>
      </tr>
    ))
  }

  return (
    <div style={{ marginTop: '40px' }}>
      <div className=''>
        <div className='card-header '>
          <div className=''>
            <h3> Plan History</h3>
            <div className='fs-6 fw-semibold text-gray-500'>See history of your plan</div>
          </div>
        </div>

        <div className='tab-content'>
          <div id='kt_billing_months' className='card-body p-0 tab-pane fade show active'>
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle gy-4 gs-9'>
                <thead className='border-bottom border-gray-200 fs-6 fw-bold bg-light bg-opacity-75'>
                  <tr>
                    <td className='min-w-100px'>Plan Name</td>
                    <td className='min-w-100px'>Plan Start Date</td>
                    <td className='min-w-100px'>Plan Expiry Date</td>
                    <td className='min-w-100px'>Current Status</td>
                  </tr>
                </thead>
                <tbody className='fw-semibold text-gray-600'>{renderTableRows(plansData)}</tbody>
              </table>
            </div>
            <div className='row m-2'>
              <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start dt-toolbar'></div>
              <div className='col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end'>
                <div className='dt-paging paging_simple_numbers'>
                  <div
                    className='dataTables_paginate paging_simple_numbers'
                    id='kt_table_users_paginate'
                  >
                    <ul className='pagination pb-2'>
                      <li
                        className={`paginate_button page-item ${
                          currentPage === 1 ? 'disabled' : ''
                        }`}
                      >
                        <button
                          type='button'
                          aria-controls='kt_table_users'
                          data-dt-idx='0'
                          className='btn primary page-link'
                          onClick={prevPage}
                        >
                          <i className='previous'></i>
                        </button>
                      </li>
                      {generatePaginationNumbers().map((pageNumber) => (
                        <li
                          key={pageNumber}
                          className={`paginate_button page-item ${
                            currentPage === pageNumber ? 'active' : ''
                          }`}
                        >
                          <button
                            type='button'
                            aria-controls='kt_table_users'
                            data-dt-idx={pageNumber}
                            className='btn primary page-link'
                            onClick={() => paginate(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      ))}
                      <li
                        className={`paginate_button page-item ${
                          currentPage === totalPages ? 'disabled' : ''
                        }`}
                      >
                        <button
                          type='button'
                          aria-controls='kt_table_users'
                          data-dt-idx='0'
                          className='btn primary page-link'
                          onClick={nextPage}
                        >
                          <i className='next'></i>
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Index
